/* @flow */
import * as React from 'react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import Moment from 'react-moment';

import { ComponentWithInjectedProps } from 'utils/injectedProps';
import { LicenseTypes } from 'forms/LicenseRequestForm/LicenseRequestForm';

import styles from './LicenseRequest.module.scss';

export type LicenseRequestProps = {
  data: Object,
};

type InjectedProps = TranslatorProps;

const row = (label: string, value: React.ReactNode) => (
  <div className={styles.contentRow}>
    <div className={styles.label}>
      <b>{label}:</b>
    </div>
    <div>{value}</div>
  </div>
);

@translate()
export default class LicenseRequest extends ComponentWithInjectedProps<
  LicenseRequestProps,
  {},
  InjectedProps
> {
  render() {
    const {
      t,
      data: {
        details: {
          asset_request: {
            license_request_type,
            unique_license_request_id,
            requested_by,
            manufacturer,
            product,
            product_description,
            date_required,
            current_asset_number,
            current_serial_number,
            current_key_code,
            new_asset_number,
            new_serial_number,
            po_number,
            comments,
            account: { name: account_name },
          },
        },
      },
    } = this.props;

    const optionalSections: React.ReactNode[] = [];

    if (
      license_request_type === LicenseTypes.REMOVE_CURRENT_LICENSE ||
      license_request_type === LicenseTypes.TRANSFER_EXISTING_LICENSE
    ) {
      optionalSections.push(
        <section key="currentAssetDetailsSection">
          <h3>{t('general:sent_request_review.license_request.headings.current_asset_details')}</h3>
          {row(
            t('general:sent_request_review.license_request.labels.asset_number'),
            current_asset_number
          )}
          {row(
            t('general:sent_request_review.license_request.labels.serial_number'),
            current_serial_number
          )}
          {license_request_type === LicenseTypes.REMOVE_CURRENT_LICENSE &&
            row(t('general:sent_request_review.license_request.labels.key_code'), current_key_code)}
        </section>
      );
    }

    if (
      license_request_type === LicenseTypes.REQUEST_NEW_LICENSE ||
      license_request_type === LicenseTypes.TRANSFER_EXISTING_LICENSE
    ) {
      optionalSections.push(
        <section key="newAssetDetailsSection">
          <h3>{t('general:sent_request_review.license_request.headings.new_asset_details')}</h3>
          {row(
            t('general:sent_request_review.license_request.labels.asset_number'),
            new_asset_number
          )}
          {row(
            t('general:sent_request_review.license_request.labels.serial_number'),
            new_serial_number
          )}
        </section>
      );
    }

    return (
      <div className={styles.licenseRequest}>
        <h1>{t('general:sent_request_review.license_request.title')}</h1>
        <section>
          {row(
            t('general:sent_request_review.license_request.labels.request_id'),
            unique_license_request_id
          )}
          {row(t('general:sent_request_review.license_request.labels.account'), account_name)}
          {row(t('general:sent_request_review.license_request.labels.requested_by'), requested_by)}
        </section>
        <section>
          {row(
            t('general:sent_request_review.license_request.labels.request_type'),
            license_request_type
          )}
          {row(
            t('general:sent_request_review.license_request.labels.license_details'),
            [manufacturer, product, product_description].join(', ')
          )}
          {row(
            t('general:sent_request_review.license_request.labels.date_required'),
            <Moment date={date_required} format="MMMM DD, YYYY" />
          )}
        </section>

        {optionalSections}

        <section>
          {row(t('general:sent_request_review.license_request.labels.po_number'), po_number)}
          {row(t('general:sent_request_review.license_request.labels.comments'), <p>{comments}</p>)}
        </section>
      </div>
    );
  }
}
